
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Linkedin, Twitter, Facebook } from 'lucide-react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Purpose from './components/Purpose';
import StrategicAdvantage from './components/StrategicAdvantage';
import Services from './components/Services';
import Values from './components/Values';
import WhyUs from './components/WhyUs';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import WhatsAppButton from './components/WhatsAppButton';
import { COMPANY_NAME, LOCATION, CR_NUMBER, EMAIL, WEBSITE, PHONE, LINKEDIN_URL, X_URL, FACEBOOK_URL } from './constants';

const Footer: React.FC = () => (
  <footer className="bg-white border-t border-slate-100 py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
        <div className="md:col-span-2">
          <span className="text-4xl font-black tracking-tighter text-slate-900">AVUS</span>
          <p className="mt-6 text-slate-500 font-medium max-w-sm leading-relaxed">
            Professional business support and regulatory coordination services for the Qatar market. 
            Licensed by MOCI.
          </p>
          <div className="mt-4 flex flex-col items-start">
            <span className="px-3 py-1 bg-slate-50 border border-slate-100 rounded-lg text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1">
              Legal Status
            </span>
            <span className="text-sm font-bold text-slate-900 tracking-tight">Limited Liability Company (LLC)</span>
          </div>
          <div className="mt-8 flex space-x-5">
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-50 rounded-xl text-slate-400 hover:text-avus-blue hover:bg-avus-light transition-all">
              <Linkedin size={18} />
            </a>
            <a href={X_URL} target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-50 rounded-xl text-slate-400 hover:text-slate-900 hover:bg-slate-200 transition-all">
              <Twitter size={18} />
            </a>
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-50 rounded-xl text-slate-400 hover:text-[#1877F2] hover:bg-blue-50 transition-all">
              <Facebook size={18} />
            </a>
          </div>
        </div>
        <div>
          <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-6">Contact</h4>
          <ul className="space-y-4 text-sm font-bold text-slate-900">
            <li>{PHONE}</li>
            <li>{EMAIL}</li>
            <li>{WEBSITE}</li>
          </ul>
        </div>
        <div>
          <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-6">Regulatory</h4>
          <ul className="space-y-4 text-sm font-bold text-slate-900">
            <li>
              <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-slate-400 mb-0.5">Commercial Registration (CR)</span>
              {CR_NUMBER}
            </li>
            <li>
              <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-slate-400 mb-0.5">Location</span>
              {LOCATION}
            </li>
          </ul>
        </div>
      </div>
      
      <div className="pt-12 border-t border-slate-50 flex flex-col md:flex-row justify-between items-center gap-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
          © {new Date().getFullYear()} {COMPANY_NAME}
        </p>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-300 max-w-xl text-center md:text-right">
          Disclaimer: AVUS provides administrative and operational support only. 
          We are not a law firm and do not provide legal advice.
        </div>
      </div>
    </div>
  </footer>
);

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// Fixed property 'state' and 'props' access errors by using class properties and explicit React.Component inheritance
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false
  };

  public static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  public render(): ReactNode {
    const { hasError } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white p-4 text-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Something went wrong</h1>
            <p className="text-slate-600 mb-4">Unexpected error. Please refresh the page.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-slate-900 text-white px-8 py-3 rounded-full font-bold"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    
    return children;
  }
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <main className="flex-grow">
          <Hero />
          <About />
          <Purpose />
          <StrategicAdvantage />
          <Services />
          <Values />
          <WhyUs />
          <Testimonials />
          <Contact />
        </main>
        <Footer />
        <WhatsAppButton />
      </div>
    </ErrorBoundary>
  );
};

export default App;
